import clsx from "clsx";
import { BootImageUnit, MAX_LEN, PathnameUrl } from "@app/constants";
import { Package } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import { IcMinus, IcMonitor, IcPlus, IcWallet } from "@app/components/icons";
import CheckoutBar from "@app/components/ui/checkout-bar";
import {
  PcGlyph,
  btn,
  btnGhost,
  btnLg,
  btnPrimary,
  input,
  select,
  specMain,
} from "@app/components/ui";

/**
 * Nhãn, số giờ của 1 đơn vị (1 ngày = 24h, 1 tháng = 720h), các nút chọn nhanh
 * và số lượng tối đa như bản thiết kế: 720 giờ, 90 ngày, 12 tháng
 */
export const UNITS = [
  {
    unit: BootImageUnit.HOUR,
    label: "Theo giờ",
    name: "giờ",
    hours: 1,
    quick: [1, 3, 6],
    max: 720,
  },
  {
    unit: BootImageUnit.DAY,
    label: "Theo ngày",
    name: "ngày",
    hours: 24,
    quick: [1, 3, 7],
    max: 90,
  },
  {
    unit: BootImageUnit.MONTH,
    label: "Theo tháng",
    name: "tháng",
    hours: 720,
    quick: [1, 3, 6],
    max: 12,
  },
];

/** Giữ số lượng trong khoảng 1 → max của đơn vị */
export const clampAmount = (n: number, max: number) =>
  Math.min(max, Math.max(1, Math.floor(n) || 1));

/** Giá 1 đơn vị theo package, giống getMoneyPayment trong BootImageModal */
const getUnitPrice = (pkg: Package, unit: BootImageUnit) =>
  unit === BootImageUnit.HOUR
    ? pkg.price_per_hour
    : unit === BootImageUnit.DAY
    ? pkg.price_per_day
    : pkg.price_per_month;

/**
 * Tách group của package thành các lựa chọn hệ điều hành.
 * Ví dụ "G1 - Windows 11 Pro, G2 - Windows 10 Pro"
 *   => [{ group: "G1", os: "Windows 11 Pro" }, { group: "G2", os: "Windows 10 Pro" }]
 */
export const parsePackageGroup = (group = "") =>
  group
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [code, ...os] = item.split(" - ");

      return { group: code.trim(), os: os.join(" - ").trim() || code.trim() };
    });

/**
 * Group (hệ điều hành) còn máy theo available_groups của package.
 * API cũ chưa trả available_groups thì coi như còn máy (trạng thái cả gói vẫn theo is_available).
 */
export const isGroupAvailable = (pkg: Package | undefined, group: string) =>
  !pkg?.available_groups || pkg.available_groups.includes(group);

/**
 * Hệ điều hành chọn sẵn khi chọn gói: chỉ chọn khi gói có đúng 1 bản còn máy
 * (gói 1 hệ điều hành, hoặc nhiều bản nhưng chỉ 1 bản còn máy).
 * Từ 2 bản còn máy trở lên thì để trống cho khách tự chọn ("Chọn hệ điều hành (N OS)").
 */
export const defaultGroup = (pkg?: Package) => {
  const options = parsePackageGroup(pkg?.group);
  const open = options.filter((item) => isGroupAvailable(pkg, item.group));

  if (open.length === 1) return open[0].group;

  // không còn bản nào: gói 1 hệ điều hành vẫn hiện bản đó như trước
  return !open.length && options.length === 1 ? options[0].group : "";
};

interface CreateSummaryProps {
  pkg?: Package;
  discount: number;
  balance: number;
  name: string;
  group: string;
  unit: BootImageUnit;
  amount: number;
  busy: boolean;
  onName: (v: string) => void;
  onGroup: (v: string) => void;
  onUnit: (u: BootImageUnit) => void;
  onAmount: (n: number) => void;
  onUnpick: () => void;
  onCreate: () => void;
  /** Mở trang Nạp tiền với số tiền còn thiếu */
  onTopup: (need: number) => void;
  /** Màu danh mục của cấu hình đang chọn */
  color?: string;
  /** Đã tích "Tôi hiểu gói không được hoàn tiền… và đồng ý điều khoản" (như Cloud Gaming) */
  agree: boolean;
  /** Bấm Tạo máy mà chưa tích đồng ý: tô đỏ dòng đồng ý */
  agreeError?: boolean;
  /** đã bấm Tạo mà chưa nhập tên máy */
  nameError?: boolean;
  /** đã bấm Tạo mà chưa chọn hệ điều hành */
  groupError?: boolean;
  onAgree: (v: boolean) => void;
  /** id của khung (thanh thanh toán ở điện thoại cuộn tới đây); tab Cloud PC dùng id riêng vì 2 tab cùng được mount */
  anchorId?: string;
}

/** Khung "Thông tin tạo máy" bên phải danh sách cấu hình. */
function CreateSummary({
  pkg,
  discount,
  balance,
  name,
  group,
  unit,
  amount,
  busy,
  onName,
  onGroup,
  onUnit,
  onAmount,
  onUnpick,
  onCreate,
  onTopup,
  color,
  agree,
  agreeError,
  nameError,
  groupError,
  onAgree,
  anchorId = "rent-summary",
}: CreateSummaryProps) {
  const t = useT();
  const current = UNITS.find((u) => u.unit === unit) || UNITS[0];
  const price = pkg ? getUnitPrice(pkg, unit) * amount : 0;
  const newPrice = price - (price * discount) / 100;
  const setAmount = (n: number) => onAmount(clampAmount(n, current.max));
  // bản Windows đang chọn vừa hết máy (trạng thái hỏi lại mỗi 5s): báo dưới ô và khoá nút Tạo máy
  const groupFull = !!pkg && !!group && !isGroupAvailable(pkg, group);
  const groupOptions = parsePackageGroup(pkg?.group);
  const groupOs = groupOptions.find((item) => item.group === group)?.os;
  const groupMissing = !!groupError && !group;

  // Quy tắc gói chỉ thuê theo ngày, lấy từ BootImageModal
  const ruleError =
    pkg?.user_day && unit === BootImageUnit.HOUR
      ? `Gói này không hỗ trợ thuê theo giờ, bạn cần thuê tối thiểu từ ${pkg.numbers_of_day} ngày`
      : pkg?.user_day &&
        unit === BootImageUnit.DAY &&
        amount < pkg.numbers_of_day
      ? `Bạn cần thuê tối thiểu từ ${pkg.numbers_of_day} ngày`
      : "";

  return (
    <>
      <aside
        id={anchorId}
        className="sum-box sum-abs flex min-h-[570px] flex-col self-stretch rounded-[14px] border border-line bg-surface shadow-card"
      >
        {/* đúng .summary của bản thiết kế: cao bằng cột thẻ cấu hình (tối thiểu 520px), cạnh dưới thẳng hàng;
            nội dung dài hơn thì phần giữa cuộn bên trong (.sum-abs) */}
        <div className="sum-in">
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
            <h2 className="text-[15px] font-bold">{t("Thông tin tạo máy")}</h2>
            {pkg && (
              <button
                type="button"
                className={clsx(btn, btnGhost, "!h-8")}
                onClick={onUnpick}
              >
                {t("Bỏ chọn")}
              </button>
            )}
          </div>

          {/* overflow-y-auto: tên máy dài (2-3 dòng) làm nội dung cao hơn khung thì phần này cuộn,
              không tràn xuống dưới phần Thành tiền như trước (giống cloud-summary, vps-summary).
              > 1100px đệm dưới 4px: khung 570px vừa đủ cho Số giờ, đệm 10px sẽ làm hiện thanh cuộn thừa */}
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-2.5 min-[1101px]:pb-1">
            {!pkg ? (
              <p className="m-0 text-muted">
                {t("Bấm vào một thẻ máy đang sẵn sàng để bắt đầu.")}
              </p>
            ) : (
              <>
                <div className="flex items-center gap-2.5 rounded-[10px] bg-surface-2 px-2 py-1.5">
                  <span
                    style={{ ["--glow" as string]: color || "var(--brand)" }}
                    className="grid h-9 w-11 flex-none place-items-center rounded-lg pc-thumb"
                  >
                    <PcGlyph height={30} color={color} />
                  </span>
                  <div className="min-w-0">
                    {/* > 1100px khung cao theo cột thẻ (.sum-abs): tên 1 dòng để Số giờ không bị đẩy xuống vùng cuộn,
                        tên đầy đủ có trên thẻ đang chọn và khi rê chuột */}
                    <h3 className="text-sm font-bold min-[1101px]:truncate" title={pkg.name}>
                      {pkg.name}
                    </h3>
                    <p className="m-0 truncate text-xs text-muted">
                      {specMain(pkg.gpu)} · {specMain(pkg.ram)} RAM
                    </p>
                  </div>
                </div>

                <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
                  <span>{t("Tên máy")}</span>
                  <input
                    className={clsx(input, nameError && "!border-bad")}
                    maxLength={MAX_LEN.NAME}
                    placeholder={t("Ví dụ: Máy treo game")}
                    aria-invalid={!!nameError}
                    value={name}
                    onChange={(e) => onName(e.target.value)}
                  />
                  {nameError && (
                    <small className="font-medium text-bad">
                      {t("Nhập tên máy trước khi tạo.")}
                    </small>
                  )}
                </label>

                <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
                  {t("Hệ điều hành")}
                  <select
                    className={clsx(
                      select,
                      "w-full",
                      !group && "!text-muted",
                      (groupFull || groupMissing) && "!border-bad"
                    )}
                    value={group}
                    aria-invalid={groupFull || groupMissing}
                    onChange={(e) => onGroup(e.target.value)}
                  >
                    {/* gói có từ 2 bản còn máy: chưa chọn sẵn, khách tự chọn */}
                    {!group && (
                      <option value="" disabled hidden>
                        {t("Chọn hệ điều hành")} ({groupOptions.length} OS)
                      </option>
                    )}
                    {/* group hết máy: làm mờ, không chọn được, ghi (tạm hết) */}
                    {groupOptions.map((item) => {
                      const full = !isGroupAvailable(pkg, item.group);

                      return (
                        <option key={item.group} value={item.group} disabled={full} className="text-fg">
                          {full ? `${item.os} (${t("tạm hết")})` : item.os}
                        </option>
                      );
                    })}
                  </select>
                  {groupFull && (
                    <small className="font-medium text-bad">
                      {groupOs} {t("đang tạm hết máy, hãy chọn hệ điều hành khác.")}
                    </small>
                  )}
                  {groupMissing && (
                    <small className="font-medium text-bad">
                      {t("Chọn hệ điều hành trước khi tạo.")}
                    </small>
                  )}
                </label>

                <div className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
                  {t("Thời gian thuê")}
                  <div className="grid grid-cols-3 gap-1.5">
                    {UNITS.map((u) => (
                      <button
                        key={u.unit}
                        type="button"
                        onClick={() => {
                          onUnit(u.unit);

                          // đổi đơn vị mà số đang nhập vượt mức tối đa thì hạ xuống
                          if (amount > u.max) onAmount(u.max);
                        }}
                        className={clsx(
                          "flex flex-col items-center rounded-[10px] border px-1 py-[5px] leading-tight",
                          unit === u.unit
                            ? "border-brand bg-brand-soft text-brand"
                            : "border-line text-fg"
                        )}
                      >
                        <b className="whitespace-nowrap text-[12.5px]">
                          {t(u.label)}
                        </b>
                        <small className="whitespace-nowrap text-[10.5px] font-normal tabular-nums">
                          {formatMoneyVN(getUnitPrice(pkg, u.unit))}/{u.hours}h
                        </small>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="whitespace-nowrap text-[12.5px] font-semibold text-muted">
                    {t("Số")} {t(current.name)}
                  </span>
                  <div className="inline-flex items-center overflow-hidden rounded-[10px] border border-line">
                    <button
                      type="button"
                      className="grid h-[34px] w-[30px] place-items-center text-muted hover:bg-surface-2"
                      onClick={() => setAmount(amount - 1)}
                    >
                      <IcMinus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={current.max}
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      className="h-[34px] w-10 border-x border-line bg-transparent text-center font-semibold tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      className="grid h-[34px] w-[30px] place-items-center text-muted hover:bg-surface-2"
                      onClick={() => setAmount(amount + 1)}
                    >
                      <IcPlus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="ml-auto flex gap-1">
                    {current.quick.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setAmount(n)}
                        className={clsx(
                          "min-w-[30px] rounded-[7px] border px-2 py-[3px] text-xs",
                          amount === n
                            ? "border-brand text-brand"
                            : "border-line text-muted"
                        )}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {ruleError && (
                  <p className="m-0 text-[12.5px] font-medium text-warn">
                    {ruleError}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-2.5">
            <div className="flex flex-col gap-1 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px] tabular-nums">
              {pkg ? (
                <>
                  <div className="text-muted">
                    {t("Thời gian")}:{" "}
                    <b className="text-fg">
                      {amount * current.hours} {t("giờ")}
                    </b>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-line pt-1.5">
                    <span className="font-semibold">{t("Thành tiền")}</span>
                    <strong className="text-lg text-brand">
                      {formatMoneyVN(newPrice)} VND
                    </strong>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-muted">{t("Số dư hiện tại")}</span>
                  <b>{formatMoneyVN(balance)} VND</b>
                </div>
              )}
            </div>

            {pkg && (
              <label
                className={clsx(
                  "flex cursor-pointer items-start gap-2 text-[12.5px] leading-snug",
                  agreeError ? "text-bad" : "text-muted"
                )}
              >
                <input
                  type="checkbox"
                  className="mt-px h-4 w-4 flex-none accent-[var(--brand)]"
                  checked={agree}
                  onChange={(e) => onAgree(e.target.checked)}
                />
                <span>
                  {t("Tôi hiểu gói không được hoàn tiền sau khi tạo và đồng ý")}{" "}
                  <a
                    className="font-semibold text-brand"
                    href={PathnameUrl.TERMS}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("điều khoản sử dụng")}
                  </a>
                  .
                </span>
              </label>
            )}
            {pkg && newPrice > balance ? (
              <button
                type="button"
                className={clsx(btn, btnPrimary, btnLg, "w-full")}
                onClick={() => onTopup(newPrice - balance)}
              >
                <IcWallet className="h-[18px] w-[18px]" />
                {t("Nạp thêm")} {formatMoneyVN(newPrice - balance)}đ
              </button>
            ) : (
              <button
                type="button"
                className={clsx(btn, btnPrimary, btnLg, "w-full")}
                disabled={!pkg || busy || !!ruleError || groupFull}
                onClick={onCreate}
              >
                <IcMonitor className="h-[18px] w-[18px]" />
                {busy
                  ? t("Đang tạo máy…")
                  : `${t("Tạo máy ngay")}${
                      pkg ? ` · ${formatMoneyVN(newPrice)}đ` : ""
                    }`}
              </button>
            )}
          </div>
        </div>
      </aside>
      <CheckoutBar
        targetId={anchorId}
        active={!!pkg}
        name={pkg ? `${pkg.name} · ${amount} ${t(current.name)}` : ""}
        total={`${formatMoneyVN(newPrice)} VND`}
      />
    </>
  );
}

export default CreateSummary;
